import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { CheckCircle2, Lock, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    address: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  if (!items.length && !done) {
    return (
      <div className="min-h-screen bg-[#faf7f1] pt-32 px-6 text-center">
        <h2 className="font-serif text-3xl text-[#1a2b17]">Your cart is empty</h2>
        <Button onClick={() => nav('/')} className="mt-6 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Shop products</Button>
      </div>
    );
  }

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, image: i.image })),
      };
      await new Promise((r) => setTimeout(r, 1400));
      const r = await axios.post(`${API}/orders`, payload, { withCredentials: true });
      setDone(r.data.order);
      clear();
      toast({ title: 'Order placed!', description: `Order ${r.data.order.id} confirmed.` });
    } catch (err) {
      toast({ title: 'Payment failed', description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#faf7f1] pt-32 pb-20 px-6">
        <div className="max-w-xl mx-auto bg-white rounded-3xl p-10 text-center shadow-xl">
          <div className="w-16 h-16 rounded-full bg-[#e8f2e2] mx-auto flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-[#3a5a2c]" />
          </div>
          <h2 className="font-serif text-3xl text-[#1a2b17]">Thank you!</h2>
          <p className="mt-3 text-[#4a5544]">Order <span className="font-mono">{done.id}</span> confirmed. We&apos;ll ship it within 24 hours.</p>
          <div className="mt-6 text-sm text-[#7a8471]">Payment: <span className="font-medium text-[#c9743a]">MOCKED (Razorpay test)</span></div>
          <div className="flex gap-3 justify-center mt-8">
            <Button onClick={() => nav('/dashboard')} className="rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">View my orders</Button>
            <Button variant="outline" onClick={() => nav('/')} className="rounded-full">Keep shopping</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf7f1] pt-28 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => nav(-1)} className="text-sm text-[#7a8471] mb-6 inline-flex items-center gap-2 hover:text-[#3a5a2c]">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          <form onSubmit={pay} className="bg-white rounded-3xl p-8 shadow-lg space-y-4">
            <h2 className="font-serif text-2xl text-[#1a2b17]">Delivery details</h2>
            <div>
              <Label>Full name</Label>
              <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>Shipping address</Label>
              <Textarea required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 rounded-xl min-h-[100px]" />
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-[#7a8471] mb-3">
                <Lock className="w-4 h-4" /> Secure checkout via Razorpay <span className="text-[10px] tracking-wider bg-[#fce7d5] text-[#c9743a] px-2 py-0.5 rounded-full">MOCKED</span>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-13 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white py-4">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>) : (<>Pay ₹{total}</>)}
              </Button>
            </div>
          </form>

          <div className="bg-white rounded-3xl p-8 shadow-lg h-fit">
            <h3 className="font-serif text-xl text-[#1a2b17] mb-5">Order summary</h3>
            <ul className="divide-y divide-neutral-100">
              {items.map((i) => (
                <li key={i.id} className="py-3 flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="text-sm text-[#1a2b17]">{i.name}</div>
                    <div className="text-xs text-[#7a8471]">Qty {i.qty}</div>
                  </div>
                  <div className="text-sm font-medium">\u20b9{i.price * i.qty}</div>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>\u20b9{total}</span></div>
              <div className="flex justify-between text-[#7a8471]"><span>Shipping</span><span>Free</span></div>
              <div className="flex justify-between text-lg font-serif text-[#1a2b17] pt-3"><span>Total</span><span>\u20b9{total}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
